/**
 * Button - TNN's design-language wrapper around Ant Design's Button.
 *
 * =====================================================================
 * WHY THIS WRAPPER EXISTS:
 * =====================================================================
 *
 * Ant Design's Button uses ``type`` for visual variants ("primary",
 * "default", "text", "link", "dashed"). React/HTML also has a ``type``
 * attribute on ``<button>`` elements ("submit", "button", "reset").
 * These two meanings of ``type`` conflict.
 *
 * This wrapper resolves the conflict:
 *   - ``variant`` prop → maps to Antd's ``type`` (visual style)
 *   - ``type`` prop → maps to Antd's ``htmlType`` (native HTML type)
 *
 * =====================================================================
 * v4.0 FIX - TypeScript error across 4 modals:
 * =====================================================================
 *
 * Pre-fix, the ButtonProps interface Omitted ``type`` entirely, which
 * meant passing ``type="submit"`` caused a TS error: "Type '{ type:
 * string }' is not assignable to type 'IntrinsicAttributes & ButtonProps'."
 *
 * This affected NewGrantModal, PersonFormModal, ResourceDirectoryPage,
 * and BillingAdminPage - 8 TS errors total. All 8 are resolved by
 * adding ``type?: "submit" | "button" | "reset"`` back to the interface
 * and mapping it to Antd's ``htmlType`` prop (which is what Antd uses
 * for the native HTML button type).
 *
 * =====================================================================
 * VARIANT MAPPING:
 * =====================================================================
 *
 *   TNN variant    → Antd type    → Visual result
 *   "primary"      → "primary"    → Solid fill with --color-primary
 *   "default"      → "default"    → Outlined/bordered
 *   "outline"      → "default"    → Same as default (synonym)
 *   "ghost"        → "text"       → No border, text only
 *   "destructive"  → "primary"    → Solid fill + danger=true (red)
 *   "link"         → "link"       → Looks like a hyperlink
 */
import { Button as AntButton, type ButtonProps as AntButtonProps } from "antd";

import { cn } from "@/shared/lib/cn";

export interface ButtonProps extends Omit<AntButtonProps, "type" | "variant"> {
  /** TNN visual variant - maps to Antd's ``type`` prop internally. */
  variant?: "primary" | "default" | "outline" | "ghost" | "destructive" | "link";
  /**
   * Native HTML button type (submit, button, reset).
   *
   * Maps to Antd's ``htmlType`` prop. This is the attribute that
   * controls form submission behavior - ``type="submit"`` makes the
   * button submit its parent form when clicked.
   *
   * Separated from Antd's ``type`` (which controls visual style)
   * to avoid the naming collision. See the module docstring for
   * the full story.
   */
  type?: "submit" | "button" | "reset";
}

/**
 * Maps TNN variant names to Antd's ``type`` prop values.
 *
 * "outline" and "default" both map to Antd's "default" because Antd's
 * default button IS an outlined button. We keep "outline" as a synonym
 * because it's a more intuitive name in many UI frameworks.
 *
 * "destructive" maps to "primary" (solid fill) plus ``danger=true``
 * (red color). The ``isDanger`` logic below handles this.
 */
const VARIANT_TO_ANTD: Record<NonNullable<ButtonProps["variant"]>, AntButtonProps["type"]> = {
  primary: "primary",
  default: "default",
  outline: "default",
  ghost: "text",
  destructive: "primary",
  link: "link",
};

/**
 * Button primitive - the base button used throughout TNN.
 *
 * Destructures ``variant``, ``type``, ``danger``, and ``className``
 * from the props. Everything else (``onClick``, ``disabled``,
 * ``loading``, ``children``, etc.) passes through to Antd's Button
 * via ``...rest``.
 */
export function Button({ variant = "default", className, danger, type, ...rest }: ButtonProps) {
  // Map our variant name to Antd's type prop.
  const antdType = VARIANT_TO_ANTD[variant];
  // "destructive" variant automatically sets danger=true (red).
  // Explicit ``danger`` prop overrides this.
  const isDanger = danger ?? variant === "destructive";

  return (
    <AntButton
      {...rest}
      type={antdType}
      htmlType={type ?? "button"}
      danger={isDanger}
      className={cn("font-medium", className)}
    />
  );
}
